import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { errorToast, successToast } from '@components/toasts/Toasts';
import type {
  RestaurantEvent,
  RestaurantEventTier,
  RestaurantMetadata,
} from '@shared/types/restaurants';
import type { Shift } from '@shared/types/shifts';
import { CENTS_IN_DOLLAR, wholeDollarsToCents } from '@shared/utils/currency';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildNestedAddOnsPayload,
  type CreateRestaurantEventPayload,
  createRestaurantEvent,
  deleteRestaurantEvent,
  getRestaurantEvents,
  type UpdateRestaurantEventPayload,
  updateRestaurantEvent,
} from './apiHelpers';
import {
  type EventAddOnFormItem,
  type EventFormData,
  isPersistableAddOnFormItem,
  isPersistableDisclaimerFormItem,
} from './form/RestaurantEventForm';
import type { RestaurantEventPreviewOverrides } from './RestaurantEventCardPreview';
import type { ServiceFeePayload } from '@shared/types/restaurants';

function toApiTime(time: string): string {
  return time && time.length === 5 ? `${time}:00` : time;
}

function parseTotalCapacityFromForm(capacityField: string): number | null {
  const parsed = Number(capacityField);
  if (capacityField === '' || Number.isNaN(parsed) || parsed < 0) {
    return null;
  }
  return Math.max(0, Math.floor(parsed));
}

function buildServiceFeesPayload(
  formValues: EventFormData,
): ServiceFeePayload[] | undefined {
  const fees = (formValues.serviceFees ?? [])
    .filter(
      (fee) =>
        fee.label.trim() !== '' ||
        (fee.percentage !== '' && fee.percentage !== undefined),
    )
    .map((fee) => ({
      label: fee.label.trim() || 'Service fee',
      percentage:
        typeof fee.percentage === 'number' && !Number.isNaN(fee.percentage)
          ? fee.percentage
          : 0,
    }));
  return fees.length > 0 ? fees : undefined;
}

function getEventOverridesFromFormValues(
  formValues: EventFormData,
  fetchedEvent: RestaurantEvent | null,
  restaurant: RestaurantMetadata,
): RestaurantEventPreviewOverrides {
  const formImages = formValues.images;
  const startTime = toApiTime(formValues.startTime);
  const endTime = toApiTime(formValues.endTime);
  const baseShift = fetchedEvent?.shift;

  const shiftOverride: Shift = baseShift
    ? {
        ...baseShift,
        startDate: formValues.startDate,
        endDate: formValues.startDate ?? baseShift.endDate ?? null,
        startTime,
        endTime,
      }
    : {
        id: '',
        name: '',
        startDate: formValues.startDate,
        endDate: formValues.startDate || null,
        startTime,
        endTime,
        repeat: [],
        occurrences: [],
        isRecurring: false,
        restaurantId: restaurant.id,
      };

  const previewTiers: RestaurantEventTier[] = (
    formValues.ticketTiers ?? []
  ).map((tier, index) => {
    const capacity = Number(tier.capacity) || 0;
    const tableCount = Math.max(0, Number(tier.tableCount) || 0);
    const priceDecimal = parseFloat(tier.price ?? '0') || 0;
    const tierTableIds = tier.floorPlanTableIds;
    const floorPlanTableIds =
      (tierTableIds?.length && tierTableIds) ||
      Array.from({ length: tableCount }, () => '');
    return {
      id: fetchedEvent?.tiers?.[index]?.id ?? `preview-tier-${index}`,
      name: tier.name ?? '',
      description: tier.description?.trim() || null,
      capacity,
      price: Math.round(priceDecimal * CENTS_IN_DOLLAR),
      floorPlanTableIds,
      ticketsSold: 0,
    };
  });

  const tierCapacitySum = previewTiers.reduce(
    (sum, previewTier) => sum + previewTier.capacity,
    0,
  );
  const previewCapacity =
    parseTotalCapacityFromForm(formValues.capacity) ?? tierCapacitySum;

  const validServiceFees = formValues.serviceFees?.filter(
    (fee) =>
      fee.label.trim() !== '' ||
      (fee.percentage !== '' && fee.percentage !== undefined),
  );
  const serviceFeesPreview =
    (validServiceFees?.length ?? 0) > 0
      ? validServiceFees.map((fee, feeIndex) => ({
          id: `preview-fee-${feeIndex}`,
          label: fee.label.trim() || 'Service fee',
          percentage:
            typeof fee.percentage === 'number' ? fee.percentage / 100 : 0,
        }))
      : undefined;
  const previewAddOns = (formValues.addOns ?? [])
    .filter(isPersistableAddOnFormItem)
    .map((addOn, index) => {
      const priceDecimal = parseFloat(addOn.price ?? '0') || 0;
      return {
        id: addOn.id ?? `preview-addon-${index}`,
        name: addOn.name,
        description: addOn.description,
        price: Math.round(priceDecimal * CENTS_IN_DOLLAR),
        imageKey: addOn.imageKey || null,
        tiers: (addOn.tierIds ?? [])
          .map((tierId) => {
            const matchedTier = previewTiers.find(
              (previewTier) => previewTier.id === tierId,
            );
            if (!matchedTier) return null;
            return {
              id: matchedTier.id,
              name: matchedTier.name,
              price: matchedTier.price,
            };
          })
          .filter(
            (
              linkedTier,
            ): linkedTier is { id: string; name: string; price: number } =>
              linkedTier != null,
          ),
      };
    });

  return {
    name: formValues.name,
    description: formValues.description,
    shift: shiftOverride,
    capacity: previewCapacity,
    tiers: previewTiers,
    addOns: previewAddOns,
    disclaimers: (formValues.disclaimers ?? [])
      .filter(isPersistableDisclaimerFormItem)
      .map((disclaimer, index) => ({
        id: `preview-disclaimer-${index}`,
        title: disclaimer.title,
        description: disclaimer.description || null,
      })),
    ...(formValues.serviceChargePercentage !== undefined &&
      formValues.serviceChargePercentage !== '' && {
        serviceChargePercentage: formValues.serviceChargePercentage,
      }),
    ...(serviceFeesPreview !== undefined
      ? { serviceFees: serviceFeesPreview }
      : {}),
    ...(formImages?.length
      ? {
          images: formImages.map((img, imageIndex) => ({
            id: img.imageKey || `preview-${imageIndex}`,
            imageKey: img.imageKey,
          })),
        }
      : {}),
  };
}

export function useEventFormPreview(
  formValues: EventFormData,
  fetchedEvent: RestaurantEvent | null,
  restaurant: RestaurantMetadata,
): RestaurantEventPreviewOverrides {
  return useMemo(
    () => getEventOverridesFromFormValues(formValues, fetchedEvent, restaurant),
    [formValues, fetchedEvent, restaurant],
  );
}

export function useFormStepScroll(
  stepIds: readonly string[],
  isActive: boolean,
) {
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const sectionRefs = useRef(new Map<string, HTMLElement>());
  const defaultStepId = stepIds[0] ?? '';
  const [scrollContainerEl, setScrollContainerEl] =
    useState<HTMLElement | null>(null);
  const [activeStepId, setActiveStepId] = useState(defaultStepId);

  const setScrollContainerRef = useCallback((el: HTMLElement | null) => {
    (scrollContainerRef as React.MutableRefObject<HTMLElement | null>).current =
      el;
    setScrollContainerEl(el);
  }, []);

  const setSectionRef = useCallback(
    (id: string) => (el: HTMLElement | null) => {
      if (!el) return;
      sectionRefs.current.set(id, el);
    },
    [],
  );

  const scrollToStep = useCallback((id: string) => {
    const el = sectionRefs.current.get(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    if (!isActive || !scrollContainerEl) return;

    const container = scrollContainerEl;
    let observer: IntersectionObserver | null = null;

    const sections = stepIds
      .map((stepId) => sectionRefs.current.get(stepId))
      .filter((sectionEl): sectionEl is HTMLElement => sectionEl != null);

    if (sections.length > 0) {
      observer = new IntersectionObserver(
        (entries) => {
          let best: { id: string; ratio: number } | null = null;
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const id = (entry.target as HTMLElement).getAttribute(
              'data-step-id',
            );
            if (!id) continue;
            const ratio = entry.intersectionRatio;
            if (!best || ratio > best.ratio) best = { id, ratio };
          }

          if (best) setActiveStepId(best.id);
        },
        {
          root: container,
          rootMargin: '-50% 0px -50% 0px',
        },
      );
      const obs = observer;
      sections.forEach((section) => {
        obs.observe(section);
      });
    }

    return () => observer?.disconnect();
  }, [isActive, scrollContainerEl, stepIds]);

  return {
    setScrollContainerRef,
    setSectionRef,
    activeStepId,
    scrollToStep,
  };
}

const EVENT_SAVE_SUCCESS_MESSAGE = 'Event saved successfully';
const EVENT_AND_ADD_ON_SAVE_SUCCESS_MESSAGE =
  'Event and Add-On saved successfully';

type ComparableAddOn = {
  id?: string;
  name: string;
  description: string;
  price: number;
  imageKey: string;
  tierIdsKey: string;
};

function toComparableAddOnsFromServer(
  addOns: RestaurantEvent['addOns'] | undefined,
): ComparableAddOn[] {
  return (addOns ?? [])
    .map((addOn) => ({
      id: addOn.id,
      name: addOn.name.trim(),
      description: (addOn.description ?? '').trim(),
      price: addOn.price,
      imageKey: (addOn.imageKey ?? '').trim(),
      tierIdsKey: [...(addOn.tiers?.map((tier) => tier.id) ?? [])]
        .sort()
        .join(','),
    }))
    .sort((first, second) => first.id.localeCompare(second.id));
}

function toComparableAddOnsFromForm(
  addOns: EventAddOnFormItem[] | undefined,
): ComparableAddOn[] {
  return (addOns ?? [])
    .filter(isPersistableAddOnFormItem)
    .map((addOn) => ({
      id: addOn.id,
      name: addOn.name.trim(),
      description: (addOn.description ?? '').trim(),
      price: wholeDollarsToCents(Number.parseFloat(addOn.price) || 0),
      imageKey: (addOn.imageKey ?? '').trim(),
      tierIdsKey: [...(addOn.tierIds ?? [])].sort().join(','),
    }))
    .sort((first, second) => {
      const firstKey =
        first.id ?? `${first.name}\0${first.price}\0${first.tierIdsKey}`;
      const secondKey =
        second.id ?? `${second.name}\0${second.price}\0${second.tierIdsKey}`;
      return firstKey.localeCompare(secondKey);
    });
}

function eventSaveSuccessMessage(
  formValues: EventFormData,
  previousEvent: RestaurantEvent | undefined,
): string {
  const fromForm = toComparableAddOnsFromForm(formValues.addOns);
  if (previousEvent === undefined) {
    return fromForm.length > 0
      ? EVENT_AND_ADD_ON_SAVE_SUCCESS_MESSAGE
      : EVENT_SAVE_SUCCESS_MESSAGE;
  }
  const fromServer = toComparableAddOnsFromServer(previousEvent.addOns);
  const addOnsChanged = JSON.stringify(fromForm) !== JSON.stringify(fromServer);
  return addOnsChanged
    ? EVENT_AND_ADD_ON_SAVE_SUCCESS_MESSAGE
    : EVENT_SAVE_SUCCESS_MESSAGE;
}

const buildCreatePayload = (
  formValues: EventFormData,
): CreateRestaurantEventPayload => {
  const tiers = formValues.ticketTiers.map((tier) => {
    const capacity = Math.max(0, Number(tier.capacity) || 0);
    const priceDecimal = parseFloat(tier.price ?? '0') || 0;
    const floorPlanTableIds = tier.floorPlanTableIds?.filter(Boolean) ?? [];
    return {
      name: tier.name ?? '',
      description: tier.description?.trim() || null,
      price: Math.round(priceDecimal * CENTS_IN_DOLLAR),
      capacity,
      floorPlanTableIds,
    };
  });
  const tierCapacitySum = tiers.reduce((sum, tier) => sum + tier.capacity, 0);
  const capacity =
    parseTotalCapacityFromForm(formValues.capacity) ?? tierCapacitySum;
  const serviceChargePercentage =
    formValues.serviceChargePercentage !== undefined &&
    formValues.serviceChargePercentage !== ''
      ? Number(formValues.serviceChargePercentage)
      : 0;

  const serviceFees = buildServiceFeesPayload(formValues);

  return {
    name: formValues.name,
    description: formValues.description,
    location: formValues.location || null,
    directions: formValues.directions || null,
    customConfirmationMessage: formValues.customConfirmationMessage || null,
    includeTicketsLinkInConfirmation:
      formValues.includeTicketsLinkInConfirmation,
    bookingUnit: formValues.bookingUnit,
    guestEmailRequirement: formValues.guestEmailRequirement,
    startDate: formValues.startDate,
    startTime: toApiTime(formValues.startTime),
    endTime: toApiTime(formValues.endTime),
    capacity,
    serviceChargePercentage,
    imageKeys: (formValues.images ?? [])
      .map((img) => img.imageKey)
      .filter(Boolean),
    disclaimers: (formValues.disclaimers ?? [])
      .filter(isPersistableDisclaimerFormItem)
      .map((disclaimer) => ({
        title: disclaimer.title.trim(),
        description: disclaimer.description || null,
      })),
    tiers,
    ...(serviceFees ? { serviceFees } : {}),
    addOns: buildNestedAddOnsPayload(formValues.addOns),
  };
};

export const useRestaurantEventsQuery = () => {
  const { id: restaurantId } = useRestaurant();

  return useQuery({
    queryKey: ['restaurantEvents', restaurantId],
    queryFn: () => getRestaurantEvents(restaurantId),
    initialData: [],
  });
};

export const useCreateRestaurantEvent = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  const restaurant = useRestaurant();

  return useMutation({
    mutationFn: (formValues: EventFormData) =>
      createRestaurantEvent(restaurant.id, buildCreatePayload(formValues)),
    onSuccess: (_data, formValues) => {
      queryClient.invalidateQueries({
        queryKey: ['restaurantEvents', restaurant.id],
      });
      successToast({
        message: eventSaveSuccessMessage(formValues, undefined),
      });
      onSuccess();
    },
    onError: () => {
      errorToast({ message: 'Failed to save event' });
    },
  });
};

export const useUpdateRestaurantEvent = ({
  restaurantEvent,
  onSuccess,
  onWarnings,
}: {
  restaurantEvent: RestaurantEvent | undefined;
  onSuccess: () => void;
  onWarnings: (warnings: string[]) => void;
}) => {
  const queryClient = useQueryClient();
  const restaurant = useRestaurant();

  return useMutation({
    mutationFn: (formValues: EventFormData & { ignoreWarnings: boolean }) => {
      if (!restaurantEvent) {
        throw new Error('Event is required for update');
      }
      const { ignoreWarnings, ...rest } = formValues;
      const base = buildCreatePayload(rest);
      const existingTierIds =
        restaurantEvent.tiers?.map((tier) => tier.id) ?? [];
      const tiers = base.tiers.map((tier, index) => ({
        ...tier,
        ...(existingTierIds[index] && { id: existingTierIds[index] }),
      }));
      const payload: UpdateRestaurantEventPayload = {
        ...base,
        ignoreWarnings,
        shiftId: restaurantEvent.shift.id,
        tiers,
        serviceFees: buildServiceFeesPayload(rest) ?? [],
      };
      return updateRestaurantEvent(restaurant.id, restaurantEvent.id, payload);
    },
    onSuccess: (
      response: RestaurantEvent | { warnings: string[] },
      variables: EventFormData & { ignoreWarnings: boolean },
    ) => {
      if ('warnings' in response) {
        onWarnings(response.warnings);
      } else {
        queryClient.setQueryData(
          ['restaurants', restaurant.id, 'restaurantEvents', response.id],
          response,
        );
        queryClient.invalidateQueries({
          queryKey: ['restaurantEvents', restaurant.id],
        });
        const { ignoreWarnings: _ignore, ...formValues } = variables;
        successToast({
          message: eventSaveSuccessMessage(formValues, restaurantEvent),
        });
        onSuccess();
      }
    },
    onError: () => {
      errorToast({ message: 'Failed to save event' });
    },
  });
};

export const useDeleteRestaurantEvent = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  const restaurant = useRestaurant();

  return useMutation({
    mutationFn: (eventId: string) =>
      deleteRestaurantEvent(restaurant.id, eventId),
    onSuccess: (_data, eventId) => {
      queryClient.removeQueries({
        queryKey: ['restaurants', restaurant.id, 'restaurantEvents', eventId],
      });
      queryClient.invalidateQueries({
        queryKey: ['restaurantEvents', restaurant.id],
      });
      successToast({ message: 'Event deleted successfully' });
      onSuccess();
    },
    onError: () => {
      errorToast({ message: 'Failed to delete event' });
    },
  });
};
